<template>
  <IsolatedVideoPlayer :options="videoOptions" :title="title" @on-play="trackPlayClick" @on-pause="trackPauseClick" />
</template>

<script lang="ts" setup>
import { Duration } from 'luxon'
import type { PlayerOptions } from '@/entities/videoPlayer'
import type { VideoEmbed } from '~/entities/embed'

type Partner = {
  created_at: string
  id: string
  name: string
  updated_at: string
}

definePageMeta({
  layout: 'isolated-page',
})

const { $analytics } = useNuxtApp()
const route = useRoute()
const uuid = computed(() => (Array.isArray(route.params.id) ? route.params.id[0] : route.params.id))

const embed = await fetchRecording(uuid.value)
const partner = !embed.partner_id ? null : await $fetch<Partner>(`/v2/partners/${embed.partner_id}`, { ...useFetchSharedOptions() })

$analytics.stopSessionReplayTracking()

if (route.query.mode === 'preview') {
  // @ts-ignore - null the track method since we don't want analytics sent for previews
  $analytics.track = () => {}
}

if (partner) {
  $analytics.track('EmbedPartnerVideoRecordingPageLoad', {
    partnerId: partner.id,
    partnerName: partner.name,
    embedLocation: embed.embed_name,
    companyName: embed?.conference?.company?.name,
    companyId: embed?.company_id,
    deckName: embed?.name,
    deckId: embed?.conference?.deck_id,
  })
} else {
  $analytics.track('EmbedVideoRecordingPageLoad', {
    companyName: embed.conference?.company?.name,
    companyId: embed.company_id,
    deckName: embed.name,
    deckId: embed.conference?.deck_id,
  })
}

const videoOptions: PlayerOptions = {
  poster: embed?.thumbnail ?? '',
  src: embed?.download_url,
  type: 'video/mp4',
}

const title = embed?.name || undefined

onUnmounted(() => {
  $analytics.startSessionReplayTracking()
})

async function fetchRecording(uuid: string) {
  const data = await $fetch<VideoEmbed>(`v2/embed/videos/${uuid}`, {
    ...useFetchSharedOptions(),
  })

  return data
}

function trackPlayClick() {
  if (partner) {
    $analytics.track('EmbedPartnerVideoRecordingClickPlay', {
      partnerId: partner.id,
      partnerName: partner.name,
      embedLocation: embed.embed_name,
      companyName: embed?.conference?.company?.name,
      companyId: embed?.company_id,
      deckName: embed?.name,
      deckId: embed?.conference?.deck_id,
    })
  } else {
    $analytics.track('EmbedVideoRecordingClickPlay', {
      companyName: embed.conference?.company?.name,
      companyId: embed.company_id,
      deckName: embed.name,
      deckId: embed.conference?.deck_id,
    })
  }
}

function trackPauseClick(event: Event) {
  if (partner) {
    $analytics.track('EmbedPartnerVideoRecordingClickPause', {
      timeStamp: formatCurrentTime((event.target as HTMLVideoElement).currentTime),
      partnerId: partner.id,
      partnerName: partner.name,
      embedLocation: embed.embed_name,
      companyName: embed?.conference?.company?.name,
      companyId: embed?.company_id,
      deckName: embed?.name,
      deckId: embed?.conference?.deck_id,
    })
  } else {
    $analytics.track('EmbedVideoRecordingClickPause', {
      timeStamp: formatCurrentTime((event.target as HTMLVideoElement).currentTime),
      companyName: embed.conference?.company?.name,
      companyId: embed.company_id,
      deckName: embed.name,
      deckId: embed.conference?.deck_id,
    })
  }
}

function formatCurrentTime(currentTimeInSeconds: number) {
  return Duration.fromObject({ seconds: currentTimeInSeconds }).toFormat('mm:ss')
}
</script>
